import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { httpClient, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query/query-keys';

import type { PortalMission, PortalMissionDetail, PortalProfile } from './types';

/**
 * Le consultant connecté.
 *
 * `GET /me/profile` : l'identité vient du **jeton**, jamais d'un paramètre.
 * Aucune route du portail n'accepte d'identifiant de candidat — c'est ce qui
 * garantit qu'un consultant ne peut pas lire le dossier d'un autre en
 * changeant un chiffre dans l'URL.
 */
export const useProfile = (): UseQueryResult<PortalProfile, ApiError> =>
  useQuery<PortalProfile, ApiError>({
    queryKey: queryKeys.portal.profile(),
    queryFn: () => httpClient.get<PortalProfile>('/me/profile'),
    /* Le nom et la fonction ne changent pas pendant une session. */
    staleTime: 10 * 60 * 1_000,
  });

/**
 * Les missions du consultant.
 *
 * Non paginées : on en a une poignée dans sa vie, et le portail a besoin de
 * toutes pour proposer les mois à déclarer.
 */
export const useMissions = (): UseQueryResult<PortalMission[], ApiError> =>
  useQuery<PortalMission[], ApiError>({
    queryKey: queryKeys.portal.missions(),
    queryFn: () => httpClient.get<PortalMission[]>('/me/missions'),
    staleTime: 5 * 60 * 1_000,
  });

export const useMission = (id: string | undefined) =>
  useQuery<PortalMissionDetail, ApiError>({
    queryKey: [...queryKeys.portal.missions(), id],
    queryFn: () => httpClient.get<PortalMissionDetail>(`/me/missions/${id}`),
    enabled: Boolean(id),
  });
